import { Flexbox, Form } from '@/components'
import currency from '@/lib/masks/currency'
import yup from '@/lib/yupPt'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import CurrencyFormat from 'react-currency-format'
import TextareaAutosize from 'react-textarea-autosize'

const schema = yup.object().shape({
  numberOfFamilyMembers: yup.mixed().required(),
  familyIncome: yup.number().min(1).required(),
  cras: yup.string(),
  nis: yup.string(),
  benefitsReceived: yup.string(),
  otherInformation: yup.string(),
})
export default function LowIncomePicker({ lowIncome, onChange }) {
  const [active, setActive] = useState(false)

  const onClose = useCallback(() => {
    setActive(false)
  }, [])

  const onConfirm = useCallback((values) => {
    onChange(values)
    onClose()
  }, [onChange, onClose])

  return (
    <div>
      {
        lowIncome
          ? (
            <>
              <div className="bg-gray p-1 s-rounded d-flex">
                <div style={{ flex: 1 }}>
                  <div>Membros na família: <b>{lowIncome?.numberOfFamilyMembers} {lowIncome?.numberOfFamilyMembers === 1 ? 'membro' : 'membros'}</b></div>
                  <div>Renda familiar: <b>{currency(lowIncome?.familyIncome, 'R$ ')}</b></div>
                  <div>CRAS: <b>{lowIncome?.cras || '-'}</b></div>
                  <div>NIS: <b>{lowIncome?.nis || '-'}</b></div>
                  <div>Benefícios recebidos: <b>{lowIncome?.benefitsReceived || '-'}</b></div>
                </div>
                <a className="btn btn-link btn-action btn-sm ml-2 tooltip tooltip-top" data-tooltip="Editar endereço" onClick={() => setActive(true)}>
                  <i className="fas fa-edit" />
                </a>
                <a className="btn btn-link btn-action btn-sm text-error tooltip tooltip-top" data-tooltip="Remover endereço" onClick={() => onChange(null)}>
                  <i className="fas fa-trash" />
                </a>
              </div>
            </>
          )
          : (
            <a className="c-hand" onClick={() => setActive(true)}>
              <i className="fas fa-edit" />
              &nbsp;Informar dados
            </a>
          )
      }

      {
        active && (
          <fieldset>
            <Form.Formik
              initialValues={lowIncome ?? {
                numberOfFamilyMembers: '',
                familyIncome: '',
                cras: '',
                nis: '',
                benefitsReceived: '',
                otherInformation: '',
              }}
              validationSchema={schema}
              onSubmit={onChange}
              validateOnMount
            >
              {
                ({
                  touched, errors, values, setFieldValue, isValid,
                }) => (
                  <div className={`modal ${active ? 'active' : ''}`}>
                    <a type="button" className="modal-overlay" aria-label="Close" onClick={onClose} />
                    <div className="modal-container">
                      <div className="modal-header">
                        <a
                          type="button"
                          className="btn btn-clear float-right"
                          onClick={onClose}
                        />
                        <div className="modal-title h5">
                          Dados de baixa renda
                        </div>
                      </div>
                      <div className="modal-body">
                        <Flexbox.Columns className="p-2 align-center">
                          <Flexbox.Column className="col-6 col-sm-12">
                            <Form.Group className={`${touched?.numberOfFamilyMembers && errors?.numberOfFamilyMembers ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="numberOfFamilyMembers">Membros na família <span className="text-error">*</span></Form.Label>
                              <Form.ValidationField
                                type="number"
                                step="1"
                                name="numberOfFamilyMembers"
                                id="numberOfFamilyMembers"
                              />
                              {
                                touched?.numberOfFamilyMembers && errors?.numberOfFamilyMembers
                                  ? <Form.Hint className="text-error">{errors.numberOfFamilyMembers}</Form.Hint> : null
                              }
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-6 col-sm-12">
                            <Form.Group className={`${touched?.familyIncome && errors?.familyIncome ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="familyIncome">Renda familiar <span className="text-error">*</span></Form.Label>
                              <Form.ValidationField
                                name="familyIncome"
                                id="familyIncome"
                              >
                                {
                                  ({ field }) => (
                                    <CurrencyFormat
                                      className="form-input"
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      prefix="R$ "
                                      placeholder="R$ 0,00"
                                      value={field.value}
                                      fixedDecimalScale
                                      decimalScale={2}
                                      allowNegative={false}
                                      onValueChange={
                                        ({ value }) => setFieldValue('familyIncome', Number(value))
                                      }
                                    />
                                  )
                                }
                              </Form.ValidationField>
                              {
                                touched?.familyIncome && errors?.familyIncome
                                  ? <Form.Hint className="text-error">{errors.familyIncome}</Form.Hint> : null
                              }
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-6 col-sm-12">
                            <Form.Group className={`${touched?.cras && errors?.cras ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="cras">CRAS</Form.Label>
                              <Form.ValidationField
                                name="cras"
                                id="cras"
                                placeholder="Número do CRAS"
                              />
                              {
                                touched?.cras && errors?.cras
                                  ? <Form.Hint className="text-error">{errors.cras}</Form.Hint> : null
                              }
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-6 col-sm-12">
                            <Form.Group className={`${touched?.nis && errors?.nis ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="nis">NIS</Form.Label>
                              <Form.ValidationField
                                name="nis"
                                id="nis"
                                placeholder="Número do NIS"
                              />
                              {
                                touched?.nis && errors?.nis
                                  ? <Form.Hint className="text-error">{errors.nis}</Form.Hint> : null
                              }
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-12">
                            <Form.Group className={`${touched?.benefitsReceived && errors?.benefitsReceived ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="benefitsReceived">Benefícios recebidos</Form.Label>
                              <Form.ValidationField
                                name="benefitsReceived"
                                id="benefitsReceived"
                              >
                                {
                                  ({ field }) => (
                                    <TextareaAutosize
                                      name="benefitsReceived"
                                      style={{ resize: 'none' }}
                                      className="form-input"
                                      rows={1}
                                      maxRows={3}
                                      placeholder="Liste os benefícios..."
                                      // eslint-disable-next-line react/jsx-props-no-spreading
                                      {...field}
                                    />
                                  )
                                }
                              </Form.ValidationField>
                            </Form.Group>
                          </Flexbox.Column>
                          <Flexbox.Column className="col-12">
                            <Form.Group className={`${touched?.otherInformation && errors?.otherInformation ? 'has-error' : ''}`}>
                              <Form.Label htmlFor="otherInformation">Outras informações</Form.Label>
                              <Form.ValidationField
                                name="otherInformation"
                                id="otherInformation"
                              >
                                {
                                  ({ field }) => (
                                    <TextareaAutosize
                                      name="otherInformation"
                                      style={{ resize: 'none' }}
                                      className="form-input"
                                      rows={1}
                                      maxRows={3}
                                      placeholder="Liste os benefícios..."
                                      // eslint-disable-next-line react/jsx-props-no-spreading
                                      {...field}
                                    />
                                  )
                                }
                              </Form.ValidationField>
                            </Form.Group>
                          </Flexbox.Column>
                        </Flexbox.Columns>
                      </div>
                      <div className="modal-footer">
                        <button type="button" onClick={() => onConfirm(values)} disabled={!isValid} className="btn btn-primary">
                          <i className="fas fa-check" />
                          &nbsp;Confirmar
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }
            </Form.Formik>
          </fieldset>
        )
      }
    </div>
  )
}

LowIncomePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  lowIncome: PropTypes.objectOf(Object),
}

LowIncomePicker.defaultProps = {
  lowIncome: null,
}
